import React, { PropsWithChildren, useCallback } from 'react';
import MultiSelectionProvider, {
  MultiSelectionActionApi,
} from 'components/v2/MultiSelections/MultiSelectionsProvider';
import Icon from '../../../components/v2/Icon/Icon';
import { useTranslation } from 'utils/translation';
import { applyFilters } from '../../../core';
import AppFilters from '../../../core/filters';
import { Dir } from '../../dirs';
import useMultipleDirectoryFieldsForm from '../../intelligent-layer/hooks/useMultipleDirectoryFieldsForm';
import { useAppFeatures } from '../../ui/selectors/app';
import usePermissions from '../../auth/permissions';
import useConfirmation from '../../../components/v2/Confirmation/use-confirmation';
import { useDirsActions } from '../../dirs/hooks';
import { useUserIsAdmin } from '../../auth/hooks';
import Typography from '../../../components/v2/Typography/Typography';

type ReportMultiSelectionProviderProps = PropsWithChildren<{
  id?: string;
}>;

type ReportSelectionItem = Dir & { attributes?: Record<string, unknown> };

function getBulkDeleteItemLabel(item: ReportSelectionItem): string {
  if (typeof item.title === 'string' && item.title.trim()) {
    return item.title;
  }

  const attrTitle = item.attributes?.title;

  if (typeof attrTitle === 'string' && attrTitle.trim()) {
    return attrTitle;
  }

  return `#${item.id}`;
}

const ReportMultiSelectionProvider = ({
  children,
  id,
}: ReportMultiSelectionProviderProps) => {
  const { t } = useTranslation();
  const features = useAppFeatures();
  const { can } = usePermissions();
  const hasBulkUpdate = features['assets.list.bulk_update'] ?? false;
  const confirm = useConfirmation();
  const { deleteDir } = useDirsActions();
  const isAdmin = useUserIsAdmin();

  const openMultipleFieldsFormModal = useMultipleDirectoryFieldsForm();
  const openMultipleFieldsForm = (
    selection: { id: string | number; type: string }[],
  ) => {
    const dirs = selection as unknown as Dir[];
    openMultipleFieldsFormModal(dirs);
  };

  const getActions = useCallback(
    ({ clearSelection }: MultiSelectionActionApi) => {
      const openBulkDelete = async (
        selection: { id: string | number; type: string }[],
      ) => {
        const dirs = (selection as unknown as ReportSelectionItem[]).filter(
          (item) => isAdmin || item.metadata?.isEditable,
        );

        if (dirs.length === 0) {
          return;
        }

        const confirmed = await confirm({
          title: t('reports_bulk_delete_confirm_title'),
          question: t('reports_bulk_delete_confirm_question', {
            count: dirs.length,
          }),
          children: (
            <Typography align="center">
              <strong>
                {dirs.map((item) => getBulkDeleteItemLabel(item)).join(', ')}
              </strong>
            </Typography>
          ),
          onConfirm() {
            dirs.forEach((dir) => deleteDir({ id: dir.id }));
          },
        });

        if (confirmed) {
          clearSelection();
        }
      };

      const defaultMultiActions = [];

      if (hasBulkUpdate && can('update directory fields')) {
        defaultMultiActions.push({
          label: t('Edit'),
          icon: <Icon name="fa-regular fa-pencil" />,
          action: openMultipleFieldsForm,
        });
      }

      if (can('delete directory')) {
        defaultMultiActions.push({
          label: t('Delete'),
          icon: <Icon name="fa-regular fa-trash" />,
          action: openBulkDelete,
        });
      }

      return applyFilters(
        AppFilters.reports.multiActions,
        defaultMultiActions,
        {
          id,
        },
      );
    },
    [
      can,
      confirm,
      deleteDir,
      hasBulkUpdate,
      id,
      isAdmin,
      openMultipleFieldsForm,
      t,
    ],
  );

  return (
    <MultiSelectionProvider getActions={getActions}>
      {children}
    </MultiSelectionProvider>
  );
};

export default ReportMultiSelectionProvider;
